/**
 * ProFind — header account icon, driven by real Supabase auth state.
 */
(function () {
  var latestSession = null;

  function destinationFor(session) {
    return session && session.user ? '/pages/account' : '/pages/login';
  }

  function updateAccountLink(session) {
    latestSession = session;
    var link = document.querySelector('[data-profind-account-trigger]');
    var label = document.querySelector('[data-profind-account-label]');
    if (!link) return;

    if (session && session.user) {
      link.setAttribute('aria-label', 'Open my account');
      if (label) label.textContent = 'My Account';
    } else {
      link.setAttribute('aria-label', 'Sign in to your account');
      if (label) label.textContent = 'Account';
    }
  }

  function bindTrigger() {
    var trigger = document.querySelector('[data-profind-account-trigger]');
    if (!trigger || trigger.hasAttribute('data-profind-bound')) return;
    trigger.setAttribute('data-profind-bound', '');
    trigger.addEventListener('click', function () {
      window.location.href = destinationFor(latestSession);
    });
  }

  function init() {
    bindTrigger();
    if (!window.ProFindAuth) {
      window.setTimeout(init, 100);
      return;
    }
    window.ProFindAuth.onAuthStateChange(updateAccountLink);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/**
 * ProFind — authoritative pre-launch access controller.
 *
 * PRE_LAUNCH rules:
 * - Personal login is always reachable.
 * - An authenticated user may open their own Personal Account.
 * - Active ProFind admins may roam the full production storefront for QA.
 * - Everyone else remains limited to approved pre-launch/onboarding routes.
 * - Admin QA permission is decided server-side by can_access_prelaunch_qa().
 * - No query-string, cookie or localStorage bypass exists.
 */
(function () {
  'use strict';

  var endpoint = 'https://lzcpiphfoscznxfxzqqz.supabase.co/functions/v1/prelaunch-public-status';
  var generation = 0;
  var gateObserver = null;

  function pathNow() {
    return (window.location.pathname || '/').replace(/\/+$/, '') || '/';
  }

  function isLoginRoute() {
    return pathNow() === '/pages/login';
  }

  function isPersonalAccountRoute() {
    return pathNow() === '/pages/account';
  }

  function hasSession(session) {
    return !!(session && session.user);
  }

  function isAllowedPublicPrelaunchPath() {
    var p = pathNow();
    return p === '/' ||
      p === '/index' ||
      p === '/pages/list-your-business' ||
      p === '/pages/register-as-a-pro' ||
      p === '/pages/business-login' ||
      p === '/pages/business-dashboard' ||
      p === '/pages/business-back-office' ||
      p === '/pages/verify-business-email' ||
      p === '/pages/profind-admin' ||
      p === '/pages/login' ||
      p === '/pages/privacy' ||
      p === '/pages/terms' ||
      p.indexOf('/pages/list-your-business-') === 0 ||
      p.indexOf('/policies/') === 0;
  }

  /*
   * IMPORTANT: this function is deliberately idempotent. The previous build
   * wrote `hidden=true` and removed classes on every MutationObserver callback,
   * even when nothing had changed. Because the observer watched the entire
   * document subtree (including countdown text updates), those writes could
   * continuously retrigger the observer and lock the browser main thread.
   */
  function hideLegacyGate() {
    var root = document.documentElement;
    if (root &&
        (root.classList.contains('pf-prelaunch-pending') ||
         root.classList.contains('pf-prelaunch-active') ||
         root.classList.contains('pf-prelaunch-navigating'))) {
      root.classList.remove(
        'pf-prelaunch-pending',
        'pf-prelaunch-active',
        'pf-prelaunch-navigating'
      );
    }

    var gate = document.getElementById('pf-prelaunch-gate');
    if (gate && gate.hidden !== true) {
      gate.hidden = true;
    }
  }

  function stopGateObserver() {
    if (!gateObserver) return;
    try { gateObserver.disconnect(); } catch (_e) {}
    gateObserver = null;
  }

  /*
   * Watch ONLY the two attributes that can re-open the legacy launch gate.
   * Do not observe childList/subtree: the launch countdown and motion layer
   * update continuously and must never drive access-control work.
   */
  function keepGateSuppressed(shouldSuppress) {
    stopGateObserver();
    if (!shouldSuppress) return;

    hideLegacyGate();

    if (!window.MutationObserver) return;

    var root = document.documentElement;
    var gate = document.getElementById('pf-prelaunch-gate');
    if (!root && !gate) return;

    gateObserver = new MutationObserver(function () {
      hideLegacyGate();
    });

    if (root) {
      gateObserver.observe(root, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    if (gate) {
      gateObserver.observe(gate, {
        attributes: true,
        attributeFilter: ['hidden']
      });
    }
  }

  function publishAccess(adminAllowed, state, session) {
    var accountAllowed = isPersonalAccountRoute() && hasSession(session);
    var loginAllowed = isLoginRoute();

    window.__PROFIND_QA_PRELAUNCH_ACCESS = adminAllowed === true;
    window.__PROFIND_PLATFORM_STATE = state;

    keepGateSuppressed(
      state === 'LIVE' ||
      loginAllowed ||
      accountAllowed ||
      (state === 'PRE_LAUNCH' && adminAllowed === true)
    );

    try {
      window.dispatchEvent(new CustomEvent('profind:prelaunch-access', {
        detail: {
          allowed: adminAllowed === true,
          accountAllowed: accountAllowed,
          state: state
        }
      }));
    } catch (_e) {}
  }

  function dataClient() {
    if (!window.ProFindData || typeof window.ProFindData.client !== 'function') return null;
    try { return window.ProFindData.client(); } catch (_e) { return null; }
  }

  function qaCheck(session) {
    if (!hasSession(session)) {
      return Promise.resolve({ known: true, allowed: false });
    }

    var client = dataClient();
    if (!client || typeof client.rpc !== 'function') {
      return Promise.resolve({ known: false, allowed: false });
    }

    return client.rpc('can_access_prelaunch_qa').then(function (result) {
      if (result && !result.error) {
        return { known: true, allowed: result.data === true };
      }
      return { known: false, allowed: false };
    }).catch(function () {
      return { known: false, allowed: false };
    });
  }

  function qaCheckWithRetry(session, attempt) {
    attempt = attempt || 0;
    return qaCheck(session).then(function (result) {
      if (result.known || attempt >= 3) return result;
      var delay = [180, 420, 900, 1500][attempt] || 1500;
      return new Promise(function (resolve) {
        window.setTimeout(function () {
          resolve(qaCheckWithRetry(session, attempt + 1));
        }, delay);
      });
    });
  }

  function redirectDenied(session) {
    var p = pathNow();

    if (p === '/pages/account') {
      if (!hasSession(session)) {
        window.location.replace('/pages/login?return_url=' + encodeURIComponent('/pages/account'));
      }
      return;
    }

    if (!isAllowedPublicPrelaunchPath()) {
      window.location.replace('/');
    }
  }

  function evaluate(session) {
    var mine = ++generation;
    var controller = window.AbortController ? new AbortController() : null;
    var timeout = window.setTimeout(function () {
      if (controller) controller.abort();
    }, 3500);

    fetch(endpoint, {
      method: 'GET',
      mode: 'cors',
      cache: 'no-store',
      signal: controller ? controller.signal : undefined
    })
      .then(function (response) {
        if (!response.ok) throw new Error('ProFind launch status ' + response.status);
        return response.json();
      })
      .then(function (data) {
        window.clearTimeout(timeout);
        if (mine !== generation) return;

        var state = String(data.platform_state || 'PRE_LAUNCH').toUpperCase();

        if (state === 'LIVE') {
          publishAccess(false, 'LIVE', session);
          return;
        }

        if (!hasSession(session)) {
          publishAccess(false, 'PRE_LAUNCH', session);
          redirectDenied(session);
          return;
        }

        /* Keep a signed-in Personal Account stable while admin QA resolves. */
        if (isPersonalAccountRoute()) {
          publishAccess(false, 'PRE_LAUNCH', session);
        }

        qaCheckWithRetry(session, 0).then(function (result) {
          if (mine !== generation) return;

          if (result.known && result.allowed) {
            publishAccess(true, 'PRE_LAUNCH', session);
            return;
          }

          if (result.known) {
            publishAccess(false, 'PRE_LAUNCH', session);
            redirectDenied(session);
            return;
          }

          /* A temporary RPC failure is not proof that an admin is denied. */
          publishAccess(false, 'PRE_LAUNCH', session);
        });
      })
      .catch(function () {
        window.clearTimeout(timeout);
        if (mine !== generation) return;

        if (isLoginRoute() || (isPersonalAccountRoute() && hasSession(session))) {
          keepGateSuppressed(true);
        }
      });
  }

  function bind(attempt) {
    attempt = attempt || 0;
    var client = dataClient();

    if (client &&
        client.auth &&
        typeof client.auth.getSession === 'function' &&
        typeof client.auth.onAuthStateChange === 'function') {
      var hydrated = false;
      var pendingSession = null;

      client.auth.onAuthStateChange(function (_event, session) {
        if (!hydrated) {
          pendingSession = session || pendingSession;
          return;
        }
        evaluate(session || null);
      });

      client.auth.getSession().then(function (result) {
        hydrated = true;
        var session = result && result.data && result.data.session
          ? result.data.session
          : (pendingSession || null);
        evaluate(session);
      }).catch(function () {
        hydrated = true;
        evaluate(pendingSession || null);
      });
      return;
    }

    if (attempt < 100) {
      window.setTimeout(function () { bind(attempt + 1); }, 100);
    }
  }

  /* Login must always remain usable before a session exists. */
  if (isLoginRoute()) keepGateSuppressed(true);

  bind(0);
})();
